import React, { FC, useState } from 'react';
import { FormHelperText } from '@mui/material';
import { FormikHelpers, useFormik } from 'formik';
import pick from 'lodash/pick';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

import { IEditPasswordRequestDTO } from 'src/api/dtos';
import AuthRepository from 'src/api/repositories/common/v1/auth.repository';
import { IError } from 'src/api/types';
import { CheckFilledIcon, CloseIcon, Modal, PasswordTextField, StrengthIndicatorType } from 'src/shared/components';
import { useTranslate } from 'src/shared/providers';
import {
  isEmployeeInfoInPasswordValid,
  isMaxRepeatingCharactersValid,
  isMaxSequentialCharactersValid,
  isMinLowerCaseValid,
  isMinNumericsValid,
  isMinSymbolsValid,
  isMinUpperCaseValid,
  isUserNameInPasswordValid,
} from 'src/shared/utils';
import { RootState } from 'src/store/types';

const DEFAULT_SETTINGS = {
  passwordComplexityEnabled: true,
  passwordMinLength: 6,
  passwordMinLowerCase: 1,
  passwordMinUpperCase: 1,
  passwordMinNumerics: 1,
  passwordMinSymbols: 1,
  passwordMaxRepeatingCharacters: 3,
  passwordMaxSequentialCharacters: 3,
};

interface Props {
  open: boolean;
  onClose: () => void;
}

export const EditPasswordModal: FC<Props> = ({ open, onClose }) => {
  const { t } = useTranslate();
  const { settings, info } = useSelector((state: RootState) => state.user);
  const [invalidCurrentPasswordErrorMessage, setInvalidCurrentPasswordErrorMessage] = useState<string | undefined>();
  const passwordComplexityEnabled =
    typeof settings?.isPasswordComplexityEnforced === 'boolean'
      ? settings.isPasswordComplexityEnforced
      : DEFAULT_SETTINGS.passwordComplexityEnabled;
  const passwordMinLength = settings?.passwordPreferredLength || DEFAULT_SETTINGS.passwordMinLength;
  const passwordMinLowerCase = settings?.passwordLowerCaseCount || DEFAULT_SETTINGS.passwordMinLowerCase;
  const passwordMinUpperCase = settings?.passwordUpperCaseCount || DEFAULT_SETTINGS.passwordMinUpperCase;
  const passwordMinNumerics = settings?.passwordNumericCharacterCount || DEFAULT_SETTINGS.passwordMinNumerics;
  const passwordMinSymbols = settings?.passwordSymbolCharacterCount || DEFAULT_SETTINGS.passwordMinSymbols;
  const passwordMaxRepeatingCharacters =
    settings?.passwordMaxRepeatCharacters || DEFAULT_SETTINGS.passwordMaxRepeatingCharacters;
  const passwordMaxSequentialCharacters =
    settings?.passwordMaxSequentialCharacters || DEFAULT_SETTINGS.passwordMaxSequentialCharacters;

  const validationSchema = Yup.object().shape({
    currentPassword: Yup.string().required(t('settings.theCurrentPasswordIsRequired')),
    password: Yup.string()
      .required(t('settings.theNewPasswordIsRequired'))
      .notOneOf([Yup.ref('currentPassword')], t('settings.theNewPasswordMustBeDifferentThanTheCurrentPassword'))
      .test(
        'isMinLength',
        (params) => t('settings.thePasswordIsMissingXCharacters', passwordMinLength - params.value.length),
        (value) => !passwordComplexityEnabled || !value || value.length >= passwordMinLength,
      )
      .test(
        'isMinLowerCaseValid',
        t('settings.thePasswordMustHaveAMinimumOfMinLowerCaseCharacters', passwordMinLowerCase),
        (value) => !passwordComplexityEnabled || !value || isMinLowerCaseValid(value, passwordMinLowerCase),
      )
      .test(
        'isMinUpperCaseValid',
        t('settings.thePasswordMustHaveAMinimumOfMinUpperCaseCharacters', passwordMinUpperCase),
        (value) => !passwordComplexityEnabled || !value || isMinUpperCaseValid(value, passwordMinUpperCase),
      )
      .test(
        'isMinNumericsValid',
        t('settings.thePasswordMustHaveAMinimumOfMinDigits', passwordMinNumerics),
        (value) => !passwordComplexityEnabled || !value || isMinNumericsValid(value, passwordMinNumerics),
      )
      .test(
        'isMinSymbolsValid',
        t('settings.thePasswordMustHaveAMinimumOfMinSpecialCharacters', passwordMinSymbols),
        (value) => !passwordComplexityEnabled || !value || isMinSymbolsValid(value, passwordMinSymbols),
      )
      .test(
        'isMaxRepeatingCharactersValid',
        t('settings.thePasswordMustNotHaveMoreThanMaxRepeatingCharacters', passwordMaxRepeatingCharacters),
        (value) =>
          !passwordComplexityEnabled || !value || isMaxRepeatingCharactersValid(value, passwordMaxRepeatingCharacters),
      )
      .test(
        'isMaxSequentialCharactersValid',
        t('settings.thePasswordMustNotHaveMoreThanMaxSequentialCharacters', passwordMaxSequentialCharacters),
        (value) =>
          !passwordComplexityEnabled ||
          !value ||
          isMaxSequentialCharactersValid(value, passwordMaxSequentialCharacters),
      )
      .test(
        'isUserNameInPasswordValid',
        t('settings.theUsernameMustNotBeIncludedInTheNewPassword'),
        (value) => !passwordComplexityEnabled || !value || isUserNameInPasswordValid(value, info?.username || ''),
      )
      .test(
        'isEmployeeInfoInPasswordValid',
        t('settings.personalInformationCannotBeIncludedInTheNewPassword'),
        (value) =>
          !passwordComplexityEnabled ||
          !value ||
          !info ||
          isEmployeeInfoInPasswordValid(value, pick(info, ['employeeCode', 'payrollCode', 'firstName', 'lastName'])),
      ),
    passwordConfirmation: Yup.string()
      .required(t('settings.theConfirmationPasswordIsRequired'))
      .oneOf([Yup.ref('password')], t('settings.theConfirmationPasswordHasToBeTheSameAsTheNewPassword')),
  });

  const submitForm = async (values: IEditPasswordRequestDTO, actions: FormikHelpers<IEditPasswordRequestDTO>) => {
    try {
      setInvalidCurrentPasswordErrorMessage(undefined);
      actions.setSubmitting(true);

      const repository = new AuthRepository();
      await repository.updatePassword(values);

      toast.info(t('settings.successYourPasswordWasUpdatedSuccessfully'), { type: toast.TYPE.SUCCESS });
    } catch (e: any) {
      const error = e as IError;
      const invalidExistingPassword = error.data?.validationErrors?.some((validation) =>
        validation.field.toLowerCase().includes('currentpassword'),
      );

      if (invalidExistingPassword) {
        setInvalidCurrentPasswordErrorMessage(t('settings.theCurrentPasswordIsIncorrect'));
        return;
      }

      toast.info(t('global.oopsAnErrorOccurred'), { type: toast.TYPE.ERROR });
    } finally {
      actions.setSubmitting(false);
    }
  };

  const { handleChange, isSubmitting, handleSubmit, values, touched, errors, resetForm, submitCount } = useFormik({
    enableReinitialize: true,
    initialValues: {
      currentPassword: '',
      password: '',
      passwordConfirmation: '',
    },
    validationSchema,
    onSubmit: submitForm,
  });

  const isNewPasswordPartiallyValid =
    isMaxRepeatingCharactersValid(values.password, passwordMaxRepeatingCharacters) &&
    isMaxSequentialCharactersValid(values.password, passwordMaxSequentialCharacters) &&
    isMinLowerCaseValid(values.password, passwordMinLowerCase) &&
    isMinSymbolsValid(values.password, passwordMinSymbols) &&
    isMinUpperCaseValid(values.password, passwordMinUpperCase) &&
    isUserNameInPasswordValid(values.password, info?.username || '');

  const handleCancel = () => {
    onClose();
    resetForm();
  };

  return (
    <Modal
      open={open}
      onClose={handleCancel}
      modalHeaderProps={{
        title: t('settings.changePassword'),
        message: t('settings.enterYourCurrentPasswordFirstInOrderToChangeYourPassword'),
        onClose: handleCancel,
      }}
      modalFooterProps={{
        closeText: t('global.cancel'),
        submitText: t('global.applyChanges'),
        closeIcon: <CloseIcon />,
        submitIcon: <CheckFilledIcon />,
        loading: isSubmitting,
        disabled: isSubmitting,
        onClose: handleCancel,
        onSubmit: handleSubmit,
      }}
    >
      <div className="w-75">
        <div className="mb-3">
          <label className="d-block mb-2 small">{t('settings.enterCurrentPassword')}</label>
          <PasswordTextField
            placeholder={t('settings.currentPassword')}
            className="mt-0 mb-0"
            variant="outlined"
            size="small"
            margin="normal"
            fullWidth
            name="password"
            autoComplete="current-password"
            value={values.currentPassword}
            error={touched.currentPassword && (!!errors.currentPassword || !!invalidCurrentPasswordErrorMessage)}
            onInput={handleChange('currentPassword')}
          />
          {touched.currentPassword && (!!errors.currentPassword || !!invalidCurrentPasswordErrorMessage) && (
            <FormHelperText error className="mb-2">
              {errors.currentPassword || invalidCurrentPasswordErrorMessage}
            </FormHelperText>
          )}
        </div>
        <div className="mb-3">
          <label className="d-block mb-2 small">{t('settings.enterNewPassword')}</label>
          <PasswordTextField
            placeholder={t('settings.newPassword')}
            className="mt-0 mb-0"
            variant="outlined"
            size="small"
            margin="normal"
            fullWidth
            name="password"
            autoComplete="password"
            value={values.password}
            error={touched.password && !!errors.password}
            onInput={handleChange('password')}
            strengthIndicatorType={
              passwordComplexityEnabled
                ? getStrengthIndicatorType(
                    values.password,
                    !errors.password,
                    isNewPasswordPartiallyValid,
                    passwordMinLength,
                  )
                : undefined
            }
          />
          {(values.password.length > 0 || submitCount > 0) && !!errors.password && (
            <FormHelperText error className="mb-2">
              {errors.password}
            </FormHelperText>
          )}
        </div>
        <div>
          <label className="d-block mb-2 small">{t('settings.reEnterNewPassword')}</label>
          <PasswordTextField
            placeholder={t('settings.reEnterNewPassword')}
            className="mt-0 mb-0"
            variant="outlined"
            size="small"
            margin="normal"
            fullWidth
            name="password"
            autoComplete="password"
            value={values.passwordConfirmation}
            error={touched.passwordConfirmation && !!errors.passwordConfirmation}
            onInput={handleChange('passwordConfirmation')}
          />
          {touched.passwordConfirmation && !!errors.passwordConfirmation && (
            <FormHelperText error className="mb-2">
              {errors.passwordConfirmation}
            </FormHelperText>
          )}
        </div>
      </div>
    </Modal>
  );
};

const getStrengthIndicatorType = (
  pwd: string,
  isValid: boolean,
  isPartialValid: boolean,
  minLength: number,
): StrengthIndicatorType | undefined => {
  if (!pwd.length) {
    return;
  }

  if (isValid) {
    return 'strong';
  }

  const digits = pwd.match(/[0-9]/g);

  if (isPartialValid && ((minLength > 6 && pwd.length >= 6) || !digits?.length)) {
    return 'medium';
  }

  return 'weak';
};
